import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, CardBody, Modal, MarkdownRenderer } from '../common';
import { CostBreakdown } from './CostBreakdown';
import { AspectSelector } from './AspectSelector';
import { MediaUploader } from './MediaUploader';
import { MediaList } from './MediaList';
import { useMySubmission, useUpdateSubmission, useSubmitSubmission, useUploadMedia, useDeleteMedia } from '../../hooks/useSubmissions';

export const SubmissionEditor = ({ gameId, roundId, aspects, balance, onSubmitted }) => {
  const { data, isLoading, error } = useMySubmission(gameId, roundId);
  const updateSubmission = useUpdateSubmission(gameId, roundId);
  const submitSubmission = useSubmitSubmission(gameId, roundId);
  const uploadMedia = useUploadMedia(gameId);
  const deleteMedia = useDeleteMedia();

  const [prose, setProse] = useState('');
  const [images, setImages] = useState([]);
  const [audio, setAudio] = useState([]);
  const [selectedAspects, setSelectedAspects] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null });

  // Initialize state from fetched data
  useEffect(() => {
    if (data?.submission) {
      setProse(data.submission.prose?.content || '');
      setImages(data.submission.images || []);
      setAudio(data.submission.audio || []);
      setSelectedAspects(data.submission.referencedAspects || []);
    } else if (data && !data.submission) {
      // No submission yet, use defaults
      setProse('');
      setImages([]);
      setAudio([]);
      setSelectedAspects([]);
    }
  }, [data]);

  // Calculate costs
  const countWords = (text) => {
    if (!text || typeof text !== 'string') return 0;
    const trimmed = text.trim();
    if (trimmed === '') return 0;
    return trimmed.split(/\s+/).length;
  };

  const wordCount = countWords(prose);
  const textCost = wordCount > 0 ? Math.max(1, Math.ceil(wordCount / 100)) : 0;
  const imageCost = images.length;
  const audioCost = audio.reduce((sum, a) => sum + Math.max(1, Math.ceil((a.durationSeconds || 0) / 30)), 0);
  const totalCost = textCost + imageCost + audioCost;

  const baseBudget = data?.budget?.base || data?.submission?.budget?.base || 0;
  const currentBudget = {
    base: baseBudget,
    bonus: 0, // No longer using bonus units
    total: baseBudget,
  };

  const cost = {
    text: textCost,
    images: imageCost,
    audio: audioCost,
    total: totalCost,
  };

  // Calculate influence cost for over-budget submission
  const isOverBudget = totalCost > baseBudget;
  const isUnderBudget = totalCost > 0 && totalCost < baseBudget;
  const overAmount = Math.max(0, totalCost - baseBudget);
  const influenceCost = overAmount;
  const canAffordOverage = balance >= influenceCost;

  const canEdit = data?.canEdit ?? true;
  const isSubmitted = data?.submission?.status === 'submitted';

  // Debounced save
  const saveChanges = useCallback(async () => {
    if (!isDirty || isSubmitted) return;

    try {
      await updateSubmission.mutateAsync({
        prose: { content: prose },
        images: images.map(i => ({ mediaId: i.id || i.mediaId })),
        audio: audio.map(a => ({ mediaId: a.id || a.mediaId })),
        referencedAspects: selectedAspects,
        bonusUnits: 0, // Always 0 - over-budget cost is paid at submission
      });
      setIsDirty(false);
    } catch (err) {
      console.error('Failed to save:', err);
    }
  }, [prose, images, audio, selectedAspects, isDirty, isSubmitted, updateSubmission]);

  // Auto-save every 5 seconds when dirty
  useEffect(() => {
    if (!isDirty) return;
    const timer = setTimeout(saveChanges, 5000);
    return () => clearTimeout(timer);
  }, [isDirty, saveChanges]);

  const handleProseChange = (e) => {
    setProse(e.target.value);
    setIsDirty(true);
  };

  const handleAspectToggle = (index, name) => {
    setSelectedAspects(prev => {
      const exists = prev.some(a => a.aspectIndex === index);
      if (exists) {
        return prev.filter(a => a.aspectIndex !== index);
      }
      return [...prev, { aspectIndex: index, aspectName: name }];
    });
    setIsDirty(true);
  };

  const handleImageUpload = async (file) => {
    try {
      const result = await uploadMedia.mutateAsync({ file, type: 'image' });
      setImages(prev => [...prev, result.media]);
      setIsDirty(true);
    } catch (err) {
      console.error('Image upload failed:', err);
    }
  };

  const handleAudioUpload = async (file) => {
    try {
      const result = await uploadMedia.mutateAsync({ file, type: 'audio' });
      setAudio(prev => [...prev, result.media]);
      setIsDirty(true);
    } catch (err) {
      console.error('Audio upload failed:', err);
    }
  };

  const handleRemoveImage = async (index) => {
    const image = images[index];
    const mediaId = image.id || image.mediaId;
    if (mediaId) {
      try {
        await deleteMedia.mutateAsync(mediaId);
      } catch (err) {
        console.error('Failed to delete image:', err);
      }
    }
    setImages(prev => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleRemoveAudio = async (index) => {
    const audioItem = audio[index];
    const mediaId = audioItem.id || audioItem.mediaId;
    if (mediaId) {
      try {
        await deleteMedia.mutateAsync(mediaId);
      } catch (err) {
        console.error('Failed to delete audio:', err);
      }
    }
    setAudio(prev => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleSubmitClick = () => {
    // Show appropriate confirmation dialog
    if (isOverBudget) {
      setConfirmDialog({ open: true, type: 'over' });
    } else if (isUnderBudget) {
      setConfirmDialog({ open: true, type: 'under' });
    } else {
      // Exactly at budget, submit directly
      performSubmit();
    }
  };

  const performSubmit = async () => {
    setConfirmDialog({ open: false, type: null });

    // Save any pending changes first
    if (isDirty) {
      await saveChanges();
    }

    try {
      // If over budget, the backend will deduct influence
      await submitSubmission.mutateAsync();
      if (onSubmitted) {
        onSubmitted();
      }
    } catch (err) {
      console.error('Failed to submit:', err);
    }
  };

  const hasAspectsToSelect = aspects && aspects.length > 0;
  const aspectsValid = !hasAspectsToSelect || selectedAspects.length > 0;
  const canSubmit = aspectsValid && totalCost > 0 && (totalCost <= baseBudget || canAffordOverage);

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <p className="text-red-600">Failed to load submission: {error.message}</p>
        </CardBody>
      </Card>
    );
  }

  if (isSubmitted) {
    return (
      <Card>
        <CardBody>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-semibold">Submission Complete</span>
            </div>
            <p className="text-gray-600">
              Your submission has been received. Waiting for other players and the GM to proceed.
            </p>
            <CostBreakdown cost={cost} budget={currentBudget} balance={balance} />
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Prose Editor */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Your Story Contribution</h3>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
              <button
                type="button"
                className={`px-3 py-1 transition-colors ${
                  !previewMode
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setPreviewMode(false)}
              >
                Write
              </button>
              <button
                type="button"
                className={`px-3 py-1 transition-colors ${
                  previewMode
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setPreviewMode(true)}
              >
                Preview
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Supports <a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Markdown</a>
          </p>
          {previewMode ? (
            <div className="w-full h-48 p-3 border rounded-lg overflow-auto bg-gray-50">
              {prose ? (
                <MarkdownRenderer content={prose} />
              ) : (
                <p className="text-gray-400 italic">Nothing to preview yet...</p>
              )}
            </div>
          ) : (
            <textarea
              value={prose}
              onChange={handleProseChange}
              disabled={!canEdit}
              placeholder="Write your contribution to the story..."
              className="w-full h-48 p-3 border rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          )}
          <div className="flex justify-between text-sm text-gray-500 mt-2">
            <span>{wordCount} words</span>
            <span>Cost: {textCost} unit{textCost !== 1 ? 's' : ''}</span>
          </div>
        </CardBody>
      </Card>

      {/* Media Attachments */}
      <Card>
        <CardBody>
          <h3 className="font-semibold mb-3">Media Attachments</h3>

          <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
            {/* Images */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Images</h4>
              <MediaList
                type="image"
                items={images}
                onRemove={handleRemoveImage}
                disabled={!canEdit}
              />
              <MediaUploader
                type="image"
                onUpload={handleImageUpload}
                disabled={!canEdit || images.length >= 20}
                uploading={uploadMedia.isPending}
              />
            </div>

            {/* Audio */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Audio</h4>
              <MediaList
                type="audio"
                items={audio}
                onRemove={handleRemoveAudio}
                disabled={!canEdit}
              />
              <MediaUploader
                type="audio"
                onUpload={handleAudioUpload}
                disabled={!canEdit || audio.length >= 10}
                uploading={uploadMedia.isPending}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Aspect Selection */}
      <Card>
        <CardBody>
          <h3 className="font-semibold mb-3">Referenced Aspects</h3>
          <AspectSelector
            availableAspects={aspects}
            selectedAspects={selectedAspects}
            onToggle={handleAspectToggle}
            disabled={!canEdit}
          />
        </CardBody>
      </Card>

      {/* Cost Breakdown */}
      <Card>
        <CardBody>
          <h3 className="font-semibold mb-3">Submission Cost</h3>
          <CostBreakdown cost={cost} budget={currentBudget} balance={balance} />
        </CardBody>
      </Card>

      {/* Submit Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-gray-500 order-2 sm:order-1">
          {isDirty && <span>Unsaved changes...</span>}
          {updateSubmission.isPending && <span>Saving...</span>}
        </div>
        <div className="flex gap-2 order-1 sm:order-2">
          <Button
            variant="secondary"
            onClick={saveChanges}
            disabled={!isDirty || updateSubmission.isPending}
            loading={updateSubmission.isPending}
          >
            Save Draft
          </Button>
          <Button
            onClick={handleSubmitClick}
            disabled={!canSubmit || submitSubmission.isPending}
            loading={submitSubmission.isPending}
          >
            Submit
          </Button>
        </div>
      </div>

      {!canSubmit && (
        <p className="text-sm text-amber-600">
          {hasAspectsToSelect && selectedAspects.length === 0 && 'Select at least one aspect. '}
          {totalCost === 0 && 'Add some content to submit. '}
          {isOverBudget && !canAffordOverage && `You need ${influenceCost} influence but only have ${balance}. `}
        </p>
      )}

      {/* Confirmation Dialog - Under Budget */}
      <Modal
        isOpen={confirmDialog.open && confirmDialog.type === 'under'}
        onClose={() => setConfirmDialog({ open: false, type: null })}
        title="Submit with Free Units Remaining?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            You have <span className="font-semibold text-green-600">{baseBudget - totalCost} free unit{baseBudget - totalCost !== 1 ? 's' : ''}</span> remaining.
            These units won't carry over to future rounds.
          </p>
          <p className="text-sm text-gray-500">
            Are you sure you want to submit without using all your free units?
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => setConfirmDialog({ open: false, type: null })}
            >
              Go Back
            </Button>
            <Button onClick={performSubmit}>
              Submit Anyway
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Dialog - Over Budget */}
      <Modal
        isOpen={confirmDialog.open && confirmDialog.type === 'over'}
        onClose={() => setConfirmDialog({ open: false, type: null })}
        title="Spend Influence to Submit?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Your submission is <span className="font-semibold text-amber-600">{overAmount} unit{overAmount !== 1 ? 's' : ''}</span> over your free budget.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-amber-800 font-medium">
              This will cost you <span className="font-bold">{influenceCost} influence</span>.
            </p>
            <p className="text-sm text-amber-700 mt-1">
              Your balance: {balance} &rarr; {balance - influenceCost}
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => setConfirmDialog({ open: false, type: null })}
            >
              Go Back
            </Button>
            <Button onClick={performSubmit}>
              Spend {influenceCost} Influence
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
